/**
 * Complete permission configuration for all modules
 * Defines all available permissions for both internal app and external API
 */

export type PermissionAction = 'view' | 'add' | 'edit' | 'delete' | 'read' | 'write'

export interface ModulePermissionConfig {
  module: string  // Internal app module name (from database)
  apiModule?: string  // External API module name (if different from module)
  relatedModules?: string[]  // Additional backend modules that get same permissions
  relatedApiModules?: string[]  // Additional API modules that get same permissions
  displayName: string
  appPermissions: PermissionAction[]  // Internal app permissions
  apiPermissions: PermissionAction[]  // External API permissions
  description: string
}

/**
 * Complete list of all modules and their available permissions
 * Order matches navigation menu structure
 */
export const PERMISSION_MODULES: ModulePermissionConfig[] = [
  {
    module: 'transaction',
    apiModule: 'transactions',  // External API uses plural
    displayName: 'Transactions',
    appPermissions: ['view', 'add', 'edit', 'delete'],
    apiPermissions: ['read', 'write'],
    description: 'Financial transactions (income and expenses)'
  },
  {
    module: 'rental',
    apiModule: 'rentals',  // External API uses plural
    displayName: 'Tenants',
    appPermissions: ['view', 'add', 'edit', 'delete'],
    apiPermissions: ['read', 'write'],
    description: 'Tenant rental agreements'
  },
  {
    module: 'house-rental',
    apiModule: 'landlord_leases',  // External API uses different name
    displayName: 'Landlords',
    appPermissions: ['view', 'add', 'edit', 'delete'],
    apiPermissions: ['read', 'write'],
    description: 'Landlord lease agreements'
  },
  {
    module: 'user',
    displayName: 'User',
    appPermissions: ['view', 'add', 'edit', 'delete'],
    apiPermissions: [],  // No external API for users
    description: 'User account management'
  },
  {
    module: 'kpi',
    displayName: 'KPI',
    appPermissions: ['view'],  // Read-only in app
    apiPermissions: ['read'],  // Read-only in API
    description: 'Key Performance Indicators and analytics'
  },
  {
    module: 'tenant',
    apiModule: 'tenants',  // External API uses plural
    displayName: 'Tenant Profile',
    appPermissions: ['view', 'add', 'edit', 'delete'],
    apiPermissions: ['read', 'write'],
    description: 'Tenant contact information'
  },
  {
    module: 'business-unit',
    apiModule: 'properties',  // External API uses different name
    relatedModules: ['room', 'company'],  // Granting this also grants room & company
    relatedApiModules: ['rooms'],  // Granting API also grants rooms API
    displayName: 'Property',
    appPermissions: ['view', 'add', 'edit', 'delete'],
    apiPermissions: ['read', 'write'],
    description: 'Rental properties, rooms, and landlord companies'
  },
  {
    module: 'transaction-code',
    displayName: 'Transaction Code',
    appPermissions: ['view', 'add', 'edit', 'delete'],
    apiPermissions: [],  // No external API
    description: 'Transaction categorization codes'
  },
  {
    module: 'capex-opex',
    displayName: 'CAPEX/OPEX',
    appPermissions: ['view', 'add', 'edit', 'delete'],
    apiPermissions: [],  // No external API
    description: 'Transaction type management'
  },
  {
    module: 'permission',
    displayName: 'Permission',
    appPermissions: ['view', 'edit'],
    apiPermissions: [],  // No external API
    description: 'Role-based access control settings'
  },
  {
    module: 'audit-log',
    displayName: 'Audit Log',
    appPermissions: ['view', 'add', 'edit', 'delete'],
    apiPermissions: [],  // No external API
    description: 'System activity audit trail'
  },
  {
    module: 'external-api',
    displayName: 'External API',
    appPermissions: ['view', 'add', 'edit', 'delete'],
    apiPermissions: [],  // Guards PAT management itself, not an API resource
    description: 'Discord bot access token (PAT) management'
  },
]

/**
 * Action descriptions for tooltips
 */
export const ACTION_DESCRIPTIONS: Record<PermissionAction, string> = {
  // App permissions
  view: 'View and access this module in the web application',
  add: 'Create new records',
  edit: 'Modify existing records',
  delete: 'Remove records (destructive action)',

  // API permissions
  read: 'Read data via External API (Discord bot)',
  write: 'Create/update/delete via External API (Discord bot)'
}

/**
 * Get all possible permissions for a given module and role
 */
export function getAllPermissionsForModule(
  moduleConfig: ModulePermissionConfig,
  roleId: number
): { action: PermissionAction; group: 'app' | 'api' }[] {
  const permissions: { action: PermissionAction; group: 'app' | 'api' }[] = []

  // Add app permissions
  moduleConfig.appPermissions.forEach(action => {
    permissions.push({ action, group: 'app' })
  })

  // Add API permissions
  moduleConfig.apiPermissions.forEach(action => {
    permissions.push({ action, group: 'api' })
  })

  return permissions
}
